import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, Clock, Users, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { waLink, BAR_NAME } from "@/lib/config";

export const ReservationForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", date: "", time: "", guests: 2, notes: "" });
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.date || !form.time) {
      toast.error("Compila nome, telefono, data e ora");
      return;
    }
    setBusy(true);
    try {
      await api.createReservation({ ...form, guests: Number(form.guests) });
      const msg =
        `Ciao ${BAR_NAME}! Vorrei prenotare un tavolo.%0A` +
        `Nome: ${form.name}%0AData: ${form.date}%0AOra: ${form.time}%0APersone: ${form.guests}` +
        (form.notes ? `%0ANote: ${form.notes}` : "");
      toast.success("Prenotazione inviata! Ti apriamo WhatsApp per la conferma.");
      window.open(waLink(decodeURIComponent(msg)), "_blank");
      setForm({ name: "", phone: "", date: "", time: "", guests: 2, notes: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "h-12 bg-zinc-900 border-zinc-800 focus-visible:ring-primary text-base";

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      data-testid="reservation-form"
      className="rounded-2xl bg-black/50 backdrop-blur-xl ring-1 ring-white/10 p-6 sm:p-8 space-y-4 shadow-[0_8px_32px_rgba(0,0,0,0.6)]"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Input data-testid="res-name" placeholder="Nome e cognome" value={form.name} onChange={set("name")} className={inputCls} />
        <Input data-testid="res-phone" type="tel" placeholder="Telefono" value={form.phone} onChange={set("phone")} className={inputCls} />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="relative">
          <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary pointer-events-none" />
          <Input data-testid="res-date" type="date" min={today} value={form.date} onChange={set("date")} className={`${inputCls} pl-10`} />
        </div>
        <div className="relative">
          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary pointer-events-none" />
          <Input data-testid="res-time" type="time" value={form.time} onChange={set("time")} className={`${inputCls} pl-10`} />
        </div>
        <div className="relative">
          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary pointer-events-none" />
          <Input data-testid="res-guests" type="number" min={1} max={30} value={form.guests} onChange={set("guests")} className={`${inputCls} pl-10`} />
        </div>
      </div>
      <Textarea data-testid="res-notes" placeholder="Note (opzionale): allergie, occasione, richieste..." value={form.notes} onChange={set("notes")} className="bg-zinc-900 border-zinc-800 focus-visible:ring-primary min-h-[80px]" />
      <Button
        data-testid="res-submit"
        type="submit"
        disabled={busy}
        className="w-full h-12 rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold text-base transition-colors duration-300"
      >
        <Send className="w-4 h-4 mr-2" />
        {busy ? "Invio..." : "Prenota e conferma su WhatsApp"}
      </Button>
    </motion.form>
  );
};
